/** Client-side permission helpers — mirrors backend presets for legacy users without a snapshot. */

export const PERMISSION_IMPLIES = {
  schedule_interviews: ['view_dashboard', 'view_templates', 'view_masters'],
  submit_decision: ['view_reports'],
  manage_templates: ['view_templates'],
  delete_templates: ['manage_templates', 'view_templates'],
  manage_resumes: ['view_resumes'],
  manage_pipeline: ['view_pipeline'],
  create_users: ['view_team'],
  edit_users: ['view_team'],
  delete_users: ['edit_users', 'view_team'],
  manage_masters: ['view_masters'],
};

const SYSTEM_ROLES = new Set(['super_admin', 'admin', 'hr_manager', 'interviewer', 'candidate', 'ml_engineer']);

export const PRESETS = {
  hr_manager: {
    permissions: [
      'view_dashboard', 'schedule_interviews', 'view_reports', 'submit_decision',
      'view_templates', 'manage_templates', 'delete_templates',
      'view_resumes', 'manage_resumes',
      'view_pipeline', 'manage_pipeline',
      'view_team', 'create_users',
      'view_masters', 'manage_settings',
      'view_plan', 'use_support',
    ],
  },
  interviewer: {
    permissions: [
      'view_dashboard', 'schedule_interviews', 'view_reports', 'submit_decision',
      'view_templates', 'manage_templates',
      'view_pipeline',
      'view_team',
      'view_masters',
      'view_plan', 'use_support',
    ],
  },
  ml_engineer: {
    permissions: [
      'view_dashboard',
      'view_templates', 'manage_templates',
      'view_reports',
      'view_masters',
      'view_plan', 'use_support',
    ],
  },
  admin: {
    permissions: [
      'view_dashboard', 'schedule_interviews', 'view_reports', 'submit_decision',
      'view_templates', 'manage_templates', 'delete_templates',
      'view_resumes', 'manage_resumes',
      'view_pipeline', 'manage_pipeline',
      'view_team', 'create_users', 'edit_users', 'delete_users',
      'view_masters', 'manage_masters', 'manage_settings',
      'view_plan', 'use_support',
    ],
  },
};

export function normalizePermissions(permissions, implies = PERMISSION_IMPLIES) {
  const allOrder = [];
  const set = new Set();
  const add = (key) => {
    if (!key || set.has(key)) return;
    set.add(key);
    allOrder.push(key);
    (implies[key] || []).forEach(add);
  };
  (permissions || []).forEach(add);
  return allOrder;
}

/** Effective permissions for a user object (snapshot, preset, or admin bypass). */
export function getEffectivePermissions(user) {
  if (!user) return [];
  if (user.role === 'admin' || user.role === 'super_admin') {
    return normalizePermissions(PRESETS.admin.permissions);
  }
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return normalizePermissions(user.permissions);
  }
  if (SYSTEM_ROLES.has(user.role) && PRESETS[user.role]) {
    return normalizePermissions(PRESETS[user.role].permissions);
  }
  if (user.portalAccess && PRESETS[user.portalAccess]) {
    return normalizePermissions(PRESETS[user.portalAccess].permissions);
  }
  return [];
}

export function hasPermissionForUser(user, permission) {
  if (!user || !permission) return false;
  if (user.role === 'admin' || user.role === 'super_admin') return true;
  return getEffectivePermissions(user).includes(permission);
}

export function hasAnyPermissionForUser(user, permissions = []) {
  if (!user || !permissions.length) return false;
  if (user.role === 'admin' || user.role === 'super_admin') return true;
  const effective = new Set(getEffectivePermissions(user));
  return permissions.some(p => effective.has(p));
}

export function togglePermission(current, key, implies = PERMISSION_IMPLIES) {
  const set = new Set(current || []);
  if (set.has(key)) {
    set.delete(key);
    const next = [...set];
    return normalizePermissions(next, implies);
  }
  set.add(key);
  return normalizePermissions([...set], implies);
}

export function countPermissions(permissions) {
  return (permissions || []).length;
}

export function presetMatches(permissions, presetPermissions, implies = PERMISSION_IMPLIES) {
  const a = new Set(normalizePermissions(permissions, implies));
  const b = new Set(normalizePermissions(presetPermissions, implies));
  if (a.size !== b.size) return false;
  for (const p of a) if (!b.has(p)) return false;
  return true;
}
